import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ClearIcon } from '../../assets/icons/close-circle-red.svg';
import { ReactComponent as ArrowIcon } from '../../assets/icons/down-arrow.svg';
import { ReactComponent as Search } from '../../assets/icons/search.svg';
import { TooltipIcon, InfoIcon } from '../tooltip/TooltipIcon';

type Option = {
  value: string | number;
  label: string;
  description?: string;
  labelNode?: React.ReactNode;
};

type FunctionInterface = (option: Option) => React.ReactNode;

type InformationAction = (option: Option) => void;

interface SelectFieldProperties {
  inputLabel?: string;
  options: Option[]; // Array of objects with value and label
  placeholder?: string;
  selectedValue: string | number;
  name: string;
  enableSearch?: boolean;
  onChangeValue: (name: string, value: any) => void;
  valueRequired?: boolean;
  error?: boolean;
  errorMessage?: string;
  svgIcon?: JSX.Element;
  fontSize?: 'small' | 'medium' | 'large';
  allowCustomInput?: boolean;
  handleCustomizedInput?: (value: string | number) => void;
  loading?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  disabledAutoSorting?: boolean; // Optional prop to disable auto-sorting of options
  tooltipText?: string;
  conditionalToolTip?: boolean;
  showTooltipForce?: boolean;
  description?: FunctionInterface;
  showInformationIcon?: boolean;
  onInformationClick?: InformationAction;
  renderInformationIcon?: (option: Option) => React.ReactNode;
  triggerOptions?: Option[];
  emptyActionLabel?: string;
  onEmptyActionClick?: () => void;
  hideLabelNodeInSelected?: boolean;
}

/**
 * Select Box filed function component
 * @param {any} param0 - The param0
 * @param {any} param0.inputLabel - The inputLabel prop
 * @param {any} param0.options - The options prop
 * @param {any} param0.name - The name prop
 * @param {any} param0.placeholder - The placeholder prop
 * @param {any} param0.selectedValue - The selectedValue prop
 * @param {any} param0.onChangeValue - The onChangeValue prop
 * @param {any} param0.error - The error prop
 * @param {any} param0.errorMessage - The errorMessage prop
 * @param {any} param0.enableSearch - The enableSearch prop
 * @param {any} param0.valueRequired - The valueRequired prop
 * @param {any} param0.svgIcon - The svgIcon prop
 * @param {any} param0.readOnly - The readOnly prop
 * @param {any} param0.fontSize - The fontSize prop
 * @param {any} param0.allowCustomInput - The allowCustomInput prop
 * @param {any} param0.handleCustomizedInput - The handleCustomizedInput prop
 * @param {any} param0.loading - The loading prop
 * @param {any} param0.disabled - The disabled prop
 * @param {any} param0.disabledAutoSorting - The disabledAutoSorting prop
 * @param {any} param0.tooltipText - The tooltipText prop
 * @param {any} param0.conditionalToolTip - The conditionalToolTip prop
 * @param {any} param0.showTooltipForce - The showTooltipForce prop
 * @param {any} param0.description - The description prop
 * @param {any} param0.showInformationIcon - The showInformationIcon prop
 * @param {any} param0.onInformationClick - The onInformationClick prop
 * @param {any} param0.renderInformationIcon - The renderInformationIcon prop
 * @param {any} param0.triggerOptions - The triggerOptions prop
 * @param {any} param0.emptyActionLabel - The emptyActionLabel prop
 * @param {any} param0.onEmptyActionClick - The onEmptyActionClick prop
 * @param {any} param0.hideLabelNodeInSelected - The hideLabelNodeInSelected prop
 * @returns {any} The rendered component.
 */
function SelectField({
  inputLabel,
  options,
  name,
  placeholder,
  selectedValue,
  onChangeValue,
  error,
  errorMessage,
  enableSearch,
  valueRequired,
  svgIcon,
  readOnly,
  fontSize = 'large',
  allowCustomInput = false,
  handleCustomizedInput,
  loading = false,
  disabled = false,
  disabledAutoSorting = false, // Default to false
  tooltipText,
  conditionalToolTip,
  showTooltipForce = false,
  description,
  showInformationIcon,
  onInformationClick,
  renderInformationIcon,
  triggerOptions,
  emptyActionLabel,
  onEmptyActionClick,
  hideLabelNodeInSelected = false,
}: Readonly<SelectFieldProperties>) {
  const reference = useRef<HTMLDivElement>(null);
  const searchInputReference = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { i18n, t } = useTranslation('sharedComponent');
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
  const [open, setOpen] = useState<string>('close');
  const [style, setStyle] = useState<any>({});
  const [valueToShow, setValueToShow] = useState<string | number>(selectedValue);
  // 🔥 Added for Search
  const [searchText, setSearchText] = useState('');

  /**
   * Handle the value change function
   * @param {any} changeValue - The changeValue
   */
  // const handleValueChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
  const handleValueChange = (changeValue: any) => {
    if (readOnly) {
      return;
    }
    onChangeValue(name, changeValue);
    setOpen('close');
  };

  /**
   * Handle the close th drop down function
   * @param {any} event - MouseEvent for outside click
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen('close');
    }
  };

  /**
   * Get the dropdown position
   * @param {any} event - The event
   */
  const openDropDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || readOnly) return;

    const target = event.target as HTMLElement;
    const isIconClicked = target.closest('.custom-icon');

    if (open === 'open') {
      if (isIconClicked || !allowCustomInput) {
        setOpen('close');
      }
      return;
    }

    const { offsetHeight } = event.currentTarget;
    let paddingValue: string | undefined;
    if (enableSearch) {
      paddingValue = direction === 'rtl' ? '14px 8px 15px 6px' : '14px 6px 15px 8px';
    }
    if (window.innerHeight > event.clientY + 300) {
      setStyle({
        top: `${inputLabel ? offsetHeight + 30 : offsetHeight}px`,
        padding: paddingValue,
      });
    } else {
      setStyle({
        bottom: `${offsetHeight}px`,
        borderBottomColor: '#6c757d',
        borderTopColor: '#e3e8f1',
        borderRadius: '8px 8px 0 0',
        padding: paddingValue,
      });
    }
    setOpen('open');
  };

  // ✅ Apply sorting only if auto-sorting is NOT disabled
  const sortedOptions = React.useMemo(() => {
    if (disabledAutoSorting) return options; // Sorting disabled → return as-is
    if (!options || options.length === 0) return [];

    // 1️⃣ Find optional “All Station” entry (by value or label)
    const allStationIndex = options.findIndex(
      (opt) => opt.value === 'all' || opt.label.toLowerCase() === 'all station',
    );

    let allStationOption: Option | null = null;
    const restOptions = [...options];

    // 2️⃣ Remove it from the list temporarily
    if (allStationIndex !== -1) {
      allStationOption = restOptions[allStationIndex];
      restOptions.splice(allStationIndex, 1);
    }

    // 3️⃣ Sort others alphabetically
    restOptions.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

    // 4️⃣ Put “All Station” on top only when it exists
    return allStationOption ? [allStationOption, ...restOptions] : restOptions;
  }, [options, disabledAutoSorting]);

  useEffect(() => {
    setValueToShow(selectedValue);
  }, [selectedValue]);

  useEffect((): void => {
    document.addEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset search text when dropdown closes
  useEffect(() => {
    if (open === 'close') {
      setSearchText('');
    }
  }, [open]);

  // Auto focus search input when dropdown opens
  useEffect(() => {
    if (open === 'open' && enableSearch && !disabled) {
      const timer = setTimeout(() => {
        searchInputReference.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open, enableSearch, disabled]);

  // Debounce functionality with 1s delay
  useEffect(() => {
    // Clear existing debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set a new debounce timer
    debounceTimer.current = setTimeout(() => {
      // pass the latest value
      if (handleCustomizedInput && selectedValue !== valueToShow && valueToShow) {
        handleCustomizedInput(valueToShow);
      }
    }, 1000); // 01 second | 1000 millisecond

    // Cleanup on unmount or valueToShow change
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [valueToShow]);

  return (
    <div className={`custom-select-wrap ${fontSize}`} dir={direction} ref={reference}>
      {inputLabel && (
        <p className={`input-label ${error && 'error'}`}>
          {inputLabel}
          {valueRequired && <span>*</span>}
          {tooltipText && conditionalToolTip && (
            <TooltipIcon text={t(tooltipText)} showTooltipForce={showTooltipForce} />
          )}
        </p>
      )}
      <div
        className={`custom-select-container ${error && 'error'} ${open}`}
        onClick={openDropDown}
        role="presentation"
        style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
        {svgIcon && <div className="frond-icon">{svgIcon}</div>}
        <div className="view-value">
          {allowCustomInput ? (
            <input
              value={valueToShow}
              onChange={(event) => !loading && setValueToShow(event.target.value)}
              className="custom-value-wrap"
              disabled={disabled}
            />
          ) : (
            <div
              className={
                selectedValue
                  ? 'select-label' // when label is shown → red
                  : '' // when placeholder is shown → black
              }
              title={(() => {
                const lookupOptions = triggerOptions || options;
                const selectedOpt = lookupOptions?.find((option) => option.value === selectedValue);
                return selectedOpt ? selectedOpt.label : '';
              })()}>
              {(() => {
                const lookupOptions = triggerOptions || options;
                const selectedOpt = lookupOptions?.find((option) => option.value === selectedValue);
                
                if (!selectedOpt) {
                  return placeholder;
                }
                
                if (!hideLabelNodeInSelected && selectedOpt.labelNode) {
                  return selectedOpt.labelNode;
                }
                
                if (selectedOpt.label && selectedOpt.label.length > 20) {
                  return `${selectedOpt.label.substring(0, 20)}...`;
                }
                
                return selectedOpt.label;
              })()}
            </div>
          )}
        </div>
        <span className="custom-icon">
          <ArrowIcon />
        </span>
      </div>
      {error ? <span className="text-danger">{errorMessage}</span> : ''}
      {open === 'open' && (
        <div className={`dropdown-items-list ${open}`} style={style}>
          {/* 🔍 Search only if enabled */}
          {enableSearch && !disabled && (
            <div className="select-box-search">
              <input
                ref={searchInputReference}
                type="text"
                className="search-box"
                placeholder="Search..."
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                onClick={(event) => event.stopPropagation()}
              />
              {searchText ? (
                <span
                  className="clear-icon"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSearchText('');
                    searchInputReference.current?.focus();
                  }}
                  role="presentation">
                  <ClearIcon />
                </span>
              ) : (
                <span>
                  <Search />
                </span>
              )}
            </div>
          )}

          {!disabled &&
            !loading &&
            sortedOptions
              .filter(
                (option) =>
                  !enableSearch || option.label.toLowerCase().includes(searchText.toLowerCase()),
              )
              .map((option) => (
                <div
                  className="items"
                  key={option.value}
                  onClick={() => handleValueChange(option.value)}
                  role="presentation">
                  <div className="item-label" title={option.label}>
                    <div className="item-label-inner">
                      {option.labelNode ||
                        (option.label && option.label.length > 20
                          ? `${option.label.substring(0, 20)}...`
                          : option.label)}
                    </div>
                    {showInformationIcon && (
                      <span
                        className="info-action"
                        onClick={(event) => {
                          event.stopPropagation();
                          onInformationClick?.(option);
                        }}
                        role="presentation">
                        {renderInformationIcon ? renderInformationIcon(option) : <InfoIcon />}
                      </span>
                    )}
                  </div>
                  {description && <span>{description(option)}</span>}
                </div>
              ))}
          {(() => {
            if (loading) {
              return <div className="items no-data">{t('loading', 'Loading...')}</div>;
            }
            const hasNoOptions = !disabled && sortedOptions.length === 0;
            const hasNoSearchResults =
              enableSearch &&
              sortedOptions.filter((opt) =>
                opt.label.toLowerCase().includes(searchText.toLowerCase()),
              ).length === 0;

            if (hasNoOptions || hasNoSearchResults) {
              return (
                <div className="items no-data">
                  {emptyActionLabel && onEmptyActionClick ? (
                    <button
                      type="button"
                      className="empty-action-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        onEmptyActionClick();
                      }}>
                      + {emptyActionLabel}
                    </button>
                  ) : (
                    t('noResultsFound', 'No results found')
                  )}
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}
    </div>
  );
}

SelectField.defaultProps = {
  inputLabel: '',
  placeholder: '',
  valueRequired: false,
  error: false,
  errorMessage: '',
  svgIcon: null,
  fontSize: 'large',
  allowCustomInput: false,
  handleCustomizedInput: null,
  loading: false,
  disabled: false,
  enableSearch: true,
  readOnly: false,
  disabledAutoSorting: false, // Default to false
  tooltipText: '',
  showTooltipForce: false,
  description: '',
  showInformationIcon: false,
  onInformationClick: undefined,
  renderInformationIcon: undefined,
  conditionalToolTip: false,
  triggerOptions: undefined,
  emptyActionLabel: '',
  onEmptyActionClick: undefined,
};

export default SelectField;
